/**
 * Unauthenticated layout shell — /auth/*
 *
 * Mirrors the prototype at apps/web/src/app/(prototype)/(unauth)/layout.tsx:
 * a fixed-overlay header whose background fades in once the user scrolls past
 * a few pixels. Inline style for the conditional bg/border keeps the behavior
 * identical to the prototype with no class-cascade surprises.
 */
import { useEffect, useState } from "react";
import { Box } from "@deliveryhero/cape-core";
import { Link, Outlet, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { ArrowBackwardIcon } from "@deliveryhero/cape-icons";
import LanguageSelector from "~/components/LanguageSelector";
import * as styles from "./layout.css";

export default function AuthLayout() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const showBack = !pathname.startsWith("/auth/login");
  const [showBackground, setShowBackground] = useState(false);

  useEffect(() => {
    function onScroll() {
      setShowBackground(window.scrollY > 4);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <Box
        as="header"
        style={{
          position: "fixed",
          top: 0,
          insetInline: 0,
          zIndex: 50,
          display: "flex",
          alignItems: "center",
          paddingInline: "var(--cp-base-spacing-2xlarge)",
          height: "57px",
          backgroundColor: showBackground
            ? "var(--cp-color-background-default, #fff)"
            : "transparent",
          borderBlockEnd: showBackground
            ? "1px solid var(--cp-color-border-default, #e5e7eb)"
            : "none",
          transition: "background-color 150ms ease",
          pointerEvents: "none",
        }}
      >
        {showBack && (
          <Link to="/auth/login" className={styles.backButton} data-testid="auth-back-btn">
            <ArrowBackwardIcon />
            {t("auth_nav.backToLogin")}
          </Link>
        )}
        <Box style={{ display: "inline-flex", pointerEvents: "auto", marginInlineStart: "auto" }}>
          <LanguageSelector />
        </Box>
      </Box>
      <Outlet />
    </>
  );
}
